import type { OrgContractDocumentItem } from '../types/contract-document.types';
import { parseSignatories } from '../types/contract-document.types';

export type DocumentStatus =
  | 'draft'
  | 'generation_completed'
  | 'generated'
  | 'preparation_success'
  | 'rollout_success'
  | 'signatory_signed_failed'
  | 'signatory_signed_success'
  | 'document_completed'
  | 'document_cancelled'
  | 'document_declined'
  | 'rollout_failed'
  | 'preparation_failed'
  | 'archived';

export const PAGE_SIZE = 5;

export interface ContractDocumentRow {
  id: string;
  title: string;
  recipientsSummary?: string;
  createdAt?: string;
  lastEditedAt?: string;
  completedAt?: string;
  status: DocumentStatus;
  progressPercent?: number;
  templateSlug?: string;
  templateTitle?: string;
  documentRaw?: string;
  formDataRaw?: string;
  /** Raw `Signatories` JSON — carried so fixed-DOCX rows can send for signature inline. */
  signatoriesRaw?: string;
  sigDocumentUrl?: string;
  sigDocumentId?: string;
  lastUpdatedDate?: string;
}

export function mapItemToRow(item: OrgContractDocumentItem): ContractDocumentRow {
  const signatories = parseSignatories(item.Signatories);
  let recipientsSummary: string | undefined;
  if (signatories.length > 0) {
    const names = signatories.map((s) => s.name).filter(Boolean);
    if (names.length <= 2) {
      recipientsSummary = `To: ${names.join(', ')}`;
    } else {
      recipientsSummary = `To: ${names[0]}, ${names[1]}, +${names.length - 2} more`;
    }
  }

  const formatDate = (iso?: string) => {
    if (!iso) return undefined;
    try {
      return new Date(iso).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return {
    id: item.ItemId,
    title: item.Name,
    recipientsSummary,
    createdAt: formatDate(item.CreatedDate),
    lastEditedAt: formatDate(item.LastUpdatedDate),
    completedAt: formatDate(item.CompletedAt),
    status: (item.Status as DocumentStatus) || 'draft',
    progressPercent: item.ProgressPercent,
    templateSlug: item.TemplateSlug,
    templateTitle: item.TemplateTitle,
    documentRaw: item.Document,
    formDataRaw: item.FormData,
    signatoriesRaw: item.Signatories,
    lastUpdatedDate: formatDate(item.LastUpdatedDate),
    sigDocumentUrl: item.SigDocumentUrl,
    sigDocumentId: item.SigDocumentId,
  };
}
